import * as React from 'react';

import {Label} from 'shells/form/label';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Radio} from 'shells/radio/radio';
import {RadioGroup} from 'shells/radio/radio-group';
import {TextField} from 'shells/textfield/textfield';

import {getHasError} from './helpers';
import type {BankValidation, BankForm} from './types';

import styles from './billing-form.module.css';

type Props = {
    bankForm: BankForm | null | undefined;
    bankValidation: BankValidation | null | undefined;
    onUpdateField: (key: string, value: string) => void;
    requireFields: boolean;
};

export function BankAccountFields(props: Props) {
    const {bankForm, bankValidation, onUpdateField, requireFields} = props;

    // Set account type to checking when the form shows up on the page, which only happens
    // when the user is required to fill out the bank account fields.
    React.useEffect(() => {
        if (requireFields) {
            if (bankForm && !bankForm.account_type) {
                onUpdateField('account_type', 'checking');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requireFields]);

    return (
        <div className={styles['bank-form-fields']}>
            <StackLegacy spacing={16}>
                <div className={styles['us-customers-warning']}>
                    <strong>* US customers only</strong>
                </div>
                <div>
                    <Label htmlFor='name_on_account'>Name on account</Label>
                    <TextField
                        // @ts-expect-error
                        name='name_on_account'
                        value={bankForm ? bankForm.name_on_account : ''}
                        required={props.requireFields}
                        hasError={
                            requireFields ? getHasError(bankValidation, 'name_on_account') : false
                        }
                        onChange={(value) => onUpdateField('name_on_account', value)}
                        selectOnFocus={true}
                    />
                </div>
                <div>
                    <Label htmlFor='account_number'>Account number</Label>
                    <TextField
                        // @ts-expect-error
                        name='account_number'
                        value={bankForm ? bankForm.account_number : ''}
                        required={props.requireFields}
                        hasError={
                            requireFields ? getHasError(bankValidation, 'account_number') : false
                        }
                        onChange={(value) => onUpdateField('account_number', value)}
                        selectOnFocus={true}
                    />
                </div>
                <div>
                    <Label htmlFor='account_number_confirmation'>Confirm account number</Label>
                    <TextField
                        // @ts-expect-error
                        name='account_number_confirmation'
                        value={bankForm ? bankForm.account_number_confirmation : ''}
                        required={props.requireFields}
                        hasError={
                            requireFields
                                ? getHasError(bankValidation, 'account_number_confirmation')
                                : false
                        }
                        errorMessage={
                            requireFields &&
                            getHasError(bankValidation, 'account_number_confirmation')
                                ? 'The account number does not match'
                                : undefined
                        }
                        onChange={(value) => onUpdateField('account_number_confirmation', value)}
                        selectOnFocus={true}
                    />
                </div>
                <div>
                    <Label htmlFor='routingNumber'>Routing number</Label>
                    <TextField
                        // @ts-expect-error
                        name='routing_number'
                        value={bankForm ? bankForm.routing_number : ''}
                        required={props.requireFields}
                        hasError={
                            requireFields ? getHasError(bankValidation, 'routing_number') : false
                        }
                        errorMessage={
                            requireFields && getHasError(bankValidation, 'routing_number')
                                ? 'The routing number entered is not valid'
                                : undefined
                        }
                        onChange={(value) => onUpdateField('routing_number', value)}
                        selectOnFocus={true}
                    />
                </div>
                <RadioGroup
                    name='account_type'
                    inline={true}
                    selectedValue={bankForm ? bankForm.account_type : 'checking'}
                    onChange={(value) => onUpdateField('account_type', value)}
                >
                    <Radio value='checking'>Checking</Radio>
                    <Radio value='savings'>Savings</Radio>
                </RadioGroup>
            </StackLegacy>
        </div>
    );
}
